// Consulta de CEP na ViaCEP (https://viacep.com.br) — autofill de endereço no
// form de representante (item 22, extensão CEP-first). fetch nativo com
// AbortController (timeout curto), sem dependência nova e sem proxy: o repo
// não define Content-Security-Policy, então o browser chama a ViaCEP direto.
//
// Contrato FAIL-OPEN de UX: qualquer falha (CEP inexistente `erro: true`,
// rede, timeout, resposta não-2xx, JSON inválido) retorna null — o form segue
// preenchível manualmente e o submit NUNCA é bloqueado por esta consulta.

import { onlyDigits } from "./representatives";

export const CEP_LOOKUP_TIMEOUT_MS = 5000;

// CEP normalizado = só dígitos, no máximo 8 (espelha maskCEP).
export function normalizeCep(v) {
  return onlyDigits(v).slice(0, 8);
}

// Completo = 8 dígitos: é o gatilho da consulta automática.
export function isCompleteCep(v) {
  return normalizeCep(v).length === 8;
}

// Mapa padrão campo-do-form → campo do endereço da ViaCEP (form de
// representante). O lookup do Novo Cedente passa o seu ({ cidade, uf }).
export const CEP_ADDRESS_FIELD_MAP = {
  street: "street",
  neighborhood: "neighborhood",
  city: "city",
  state: "state",
};

/**
 * Funde no form o endereço retornado pela ViaCEP — função PURA (StrictMode-
 * safe dentro do updater do setForm) e única para as duas superfícies
 * CEP-first (form de representante e lookup do Novo Cedente).
 *
 * Regras (fix do bug "CEP corrigido não re-preenche", feedback do owner
 * 2026-07-11 — antes só campos VAZIOS eram preenchidos, então o autofill do
 * 1º CEP bloqueava o re-preenchimento do CEP corrigido):
 * - campo que o usuário digitou NESTA sessão do form nunca é sobrescrito;
 * - os demais recebem SEMPRE o valor do lookup — inclusive sobrescrevendo o
 *   autofill do CEP anterior, e LIMPANDO o que o lookup novo não traz (ex.:
 *   CEP geral de cidade sem logradouro): endereço do CEP antigo é stale.
 * Não dispara busca nenhuma (sem risco de loop) — quem consulta é o handler
 * de mudança do campo CEP, deduplicado por último-CEP-consultado.
 */
export function mergeCepAddress(form, address, { manuallyEdited, fields = CEP_ADDRESS_FIELD_MAP } = {}) {
  const next = { ...form };
  for (const [formField, addressKey] of Object.entries(fields)) {
    if (manuallyEdited?.has(formField)) continue;
    next[formField] = (address && address[addressKey]) || "";
  }
  return next;
}

/**
 * Busca o endereço do CEP na ViaCEP e mapeia para os campos do form de
 * representante (logradouro→street, bairro→neighborhood, localidade→city,
 * uf→state). Retorna o objeto mapeado ou null (CEP incompleto/inexistente,
 * falha de rede ou timeout).
 */
export async function fetchCepAddress(cep, { timeoutMs = CEP_LOOKUP_TIMEOUT_MS } = {}) {
  const digits = normalizeCep(cep);
  if (digits.length !== 8) return null;

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetch(`https://viacep.com.br/ws/${digits}/json/`, {
      signal: controller.signal,
    });
    if (!res.ok) return null;
    const data = await res.json();
    // ViaCEP responde 200 com { erro: true } para CEP bem-formado inexistente.
    if (!data || data.erro) return null;
    return {
      street: data.logradouro || "",
      neighborhood: data.bairro || "",
      city: data.localidade || "",
      state: data.uf || "",
    };
  } catch {
    // Rede fora, CORS, abort por timeout, JSON inválido → sem prefill.
    return null;
  } finally {
    clearTimeout(timer);
  }
}
